import { createContext, useContext, useEffect, useRef, useState } from 'react';
import cdoBeing from '../cdo/cdoBeing';
import MediaImage from './MediaImage';
import { mediaSource } from '../shared/mediaSource';
import './BeingRoutinePortraits.css';

const Context = createContext(null);
const invoke = (method, data) => new Promise((resolve, reject) => cdoBeing[method](data, (error, result) =>
  error || result?.error ? reject(new Error(result?.error?.message || result?.error || 'Routine images could not be saved.')) : resolve(result)));
const same = (a, b) => String(a || '') === String(b || '');
const rowKey = (routine, index) => routine._editorKey || String(routine.idBeingRoutine || `new-${index}`);

export function RoutinePortraitProvider({ being, onChange, aogYear, primaryVersion, children }) {
  const latest = useRef({ being, onChange, aogYear }); latest.current = { being, onChange, aogYear };
  const [gallery, setGallery] = useState({ routines: [], portraits: [] });
  const [error, setError] = useState('');
  const [jobs, setJobs] = useState({});
  const [drafts, setDrafts] = useState({});
  const [picker, setPicker] = useState(null);
  const loadedBeing = useRef(null);
  useEffect(() => {
    let active = true;
    if (loadedBeing.current !== being.idEntity) {
      loadedBeing.current = being.idEntity;
      setGallery({ routines: [], portraits: [] }); setError(''); setJobs({}); setDrafts({}); setPicker(null);
    }
    if (being.idEntity) invoke('routinePortraits', being.idEntity).then(result => { if (active) setGallery(result); })
      .catch(failure => { if (active) setError(failure.message); });
    return () => { active = false; };
  }, [being.idEntity, primaryVersion]);
  const publish = (owner, result) => { if (latest.current.being.idEntity === owner) setGallery(result); };
  const selected = async (owner, routine) => {
    const result = await invoke('saveRoutinePortraitSchedule', { idEntity: owner, routine });
    const current = latest.current;
    if (current.being.idEntity === owner) current.onChange({ ...current.being, routines: (current.being.routines || []).map(row =>
      same(row.idBeingRoutine, result.routine.idBeingRoutine) || (routine.idBeingRoutine && same(row.idBeingRoutine, routine.idBeingRoutine))
        || (routine._editorKey && row._editorKey === routine._editorKey) || row === routine
        ? { ...row, idBeingRoutine: result.routine.idBeingRoutine } : row) });
    return result.routine;
  };
  const run = async (routine, index, operation, action) => {
    const owner = being.idEntity, key = rowKey(routine, index);
    setJobs(current => ({ ...current, [key]: { ...current[key], count: (current[key]?.count || 0) + 1,
      generating: operation === 'generate' || current[key]?.generating, error: '', message: '' } }));
    try { await action(owner); }
    catch (failure) { if (latest.current.being.idEntity === owner) setJobs(current => ({ ...current, [key]: { ...current[key], error: failure.message } })); }
    finally { if (latest.current.being.idEntity === owner) setJobs(current => ({ ...current, [key]: { ...current[key],
      count: Math.max(0, (current[key]?.count || 1) - 1), ...(operation === 'generate' ? { generating: false } : {}) } })); }
  };
  const metadata = routine => gallery.routines.find(row => same(row.idBeingRoutine, routine.idBeingRoutine));
  const prompt = (routine, index) => drafts[rowKey(routine, index)] ?? metadata(routine)?.portraitPrompt ?? '';
  const configure = async (owner, routine, data) => {
    const result = await invoke('configureRoutinePortrait', { idEntity: owner, routineId: routine.idBeingRoutine, ...data });
    publish(owner, result); return result;
  };
  const value = { gallery, error, jobs, picker, setPicker, prompt, metadata,
    editPrompt: (routine, index, text) => setDrafts(current => ({ ...current, [rowKey(routine, index)]: text })),
    savePrompt: (routine, index) => {
      if (routine.idBeingRoutine && prompt(routine, index) !== (metadata(routine)?.portraitPrompt || ''))
        run(routine, index, 'prompt', owner => configure(owner, routine, { prompt: prompt(routine, index), assign: false }));
    },
    generate: (routine, index) => run(routine, index, 'generate', async owner => {
      const saved = await selected(owner, routine);
      await configure(owner, saved, { prompt: prompt(routine, index), assign: false });
      const result = await invoke('generateRoutinePortrait', { idEntity: owner, routineId: saved.idBeingRoutine, aogYear: Number(aogYear) || 0 });
      publish(owner, result);
      if (latest.current.being.idEntity === owner) setJobs(current => ({ ...current, [rowKey(routine, index)]: { ...current[rowKey(routine, index)], message: result.message } }));
    }),
    choose: (routine, index, mediaId) => run(routine, index, 'assign', async owner => {
      const saved = await selected(owner, routine);
      await configure(owner, saved, { assign: true, mediaId, expectedMediaId: metadata(routine)?.portraitMediaId || null });
      if (latest.current.being.idEntity === owner) setPicker(null);
    }),
    remove: (routine, index) => run(routine, index, 'remove', owner => configure(owner, routine,
      { assign: true, remove: true, mediaId: null, expectedMediaId: metadata(routine)?.portraitMediaId || null })),
    deleteImage: (routine, index, mediaId) => run(routine, index, 'delete', async owner =>
      publish(owner, await invoke('deleteRoutinePortrait', { idEntity: owner, mediaId }))),
    openPicker: (routine, index) => run(routine, index, 'gallery', async owner => {
      publish(owner, await invoke('routinePortraits', owner));
      if (latest.current.being.idEntity === owner) setPicker(rowKey(routine, index));
    }),
  };
  return <Context.Provider value={value}>{error && <p className="panel-status error-status">Routine images: {error}</p>}{children}</Context.Provider>;
}

export function BeingRoutinePortrait({ routine, index, beingId }) {
  const context = useContext(Context);
  if (!context) return null;
  const { gallery, metadata, prompt, jobs } = context;
  const key = rowKey(routine, index), job = jobs[key] || {};
  const primary = gallery.portraits.find(row => row.isPrimary) || gallery.portraits.find(row => row.variantKey === 'primary') || gallery.portraits[0];
  const assigned = metadata(routine)?.portraitMediaId;
  const image = gallery.portraits.find(row => same(row.idEntityMedia, assigned)) || primary;
  const count = assigned ? gallery.routines.filter(row => same(row.portraitMediaId, assigned)).length : 0;
  return <section className="routine-portrait" aria-label={`Routine ${index + 1} image`}>
    <div className="routine-portrait-preview">{image ? <MediaImage src={mediaSource(image, 'card')} alt={`Routine ${index + 1} portrait`} /> : <span>No saved primary image</span>}
      <small>{assigned ? `Override · used by ${count} routine${count === 1 ? '' : 's'}` : 'Using primary image'}</small></div>
    <div className="routine-portrait-options">
      <label className="editor-field"><span className="field-label">Image instructions (optional)</span>
        <textarea aria-label={`Routine ${index + 1} image instructions`} maxLength={4000} rows={2} value={prompt(routine, index)}
          onChange={event => context.editPrompt(routine, index, event.target.value)} onBlur={() => context.savePrompt(routine, index)}
          placeholder="For example: off duty, without armor, wearing a blue dress." /></label>
      <div className="routine-portrait-actions">
        <button type="button" className="secondary-button" disabled={!beingId || !primary || Boolean(job.generating)} onClick={() => context.generate(routine, index)}>
          {job.generating ? 'Generating image…' : 'Generate Image'}</button>
        <button type="button" className="secondary-button" disabled={!beingId} onClick={() => context.openPicker(routine, index)}>Use Existing Image</button>
        <button type="button" className="secondary-button danger-button" disabled={!assigned} onClick={() => context.remove(routine, index)}>Delete Override</button>
      </div>
      {!beingId ? <small>Save the Being first.</small> : !primary && <small>Create and save the primary portrait first.</small>}
      {job.error && <p role="alert" className="error-status">{job.error}</p>}{job.message && <p role="status">{job.message}</p>}
      {context.picker === key && <div className="routine-portrait-picker" aria-label="Choose an existing portrait">
        {gallery.portraits.map((portrait, i) => <div key={portrait.idEntityMedia}><button type="button" className="secondary-button"
          onClick={() => context.choose(routine, index, portrait.idEntityMedia)}>
          <MediaImage src={mediaSource(portrait, 'card')} alt={`Saved portrait ${i + 1}`} />
          <span>{same(portrait.idEntityMedia, primary?.idEntityMedia) ? 'Primary portrait' : `Portrait ${i + 1}`}</span>
        </button>{portrait.variantKey?.startsWith('routine:') && !portrait.isPrimary
          && !gallery.routines.some(row => same(row.portraitMediaId, portrait.idEntityMedia))
          && <button type="button" className="secondary-button danger-button" onClick={() => context.deleteImage(routine, index, portrait.idEntityMedia)}>Delete portrait {i + 1}</button>}</div>)}
        <button type="button" className="secondary-button" onClick={() => context.setPicker(null)}>Close image picker</button>
      </div>}
    </div>
  </section>;
}
